/**
 * Smooth Scroll with Inertia Effect
 */

class SmoothScroll {
  constructor(options = {}) {
    this.options = {
      ease: options.ease || 0.08, // 0 ~ 1 (낮을수록 부드럽고 느림)
      smooth: options.smooth !== undefined ? options.smooth : true,
      direction: options.direction || "vertical",
      gestureDirection: options.gestureDirection || "vertical",
      smoothTouch: options.smoothTouch || false,
      touchMultiplier: options.touchMultiplier || 2,
      infinite: options.infinite || false,
    };

    this.data = {
      ease: this.options.ease,
      current: 0,
      target: 0,
      last: 0,
      rounded: 0,
    };

    this.scrollWrapper =
      document.querySelector("[data-scroll-container]") || document.body;
    this.dom = { content: this.scrollWrapper };

    this.init();
  }

  init() {
    if (!this.options.smooth) return;

    // Setup
    this.setStyles();
    this.bindMethods();
    this.addEvents();
    this.requestAnimationFrameId = null;
    this.isRunning = false;
    this.isScrolling = false;
    this.scrollTimeout = null;

    // Start animation loop
    this.run();
  }

  setStyles() {
    document.body.style.cssText =
      "position: fixed; top: 0; left: 0; width: 100%; height: 100%; overflow: hidden;";

    this.dom.content.style.cssText =
      "position: fixed; top: 0; left: 0; width: 100%;";
  }

  bindMethods() {
    [
      "resize",
      "run",
      "onWheel",
      "onTouchStart",
      "onTouchMove",
      "onTouchEnd",
    ].forEach((method) => {
      this[method] = this[method].bind(this);
    });
  }

  addEvents() {
    window.addEventListener("resize", this.resize, { passive: true });

    // Wheel event for desktop
    window.addEventListener("wheel", this.onWheel, { passive: false });

    // Touch events for mobile
    if (this.options.smoothTouch) {
      window.addEventListener("touchstart", this.onTouchStart, {
        passive: true,
      });
      window.addEventListener("touchmove", this.onTouchMove, {
        passive: false,
      });
      window.addEventListener("touchend", this.onTouchEnd, { passive: true });
    }

    this.resize();
  }

  resize() {
    this.windowHeight = window.innerHeight;
    this.windowWidth = window.innerWidth;
    this.scrollHeight =
      this.dom.content.scrollHeight || this.dom.content.offsetHeight;
    this.maxScroll = this.scrollHeight - this.windowHeight;
  }

  onWheel(e) {
    e.preventDefault();

    const delta = e.deltaY;
    this.data.target += delta;
    this.clampTarget();

    this.startScrolling();
  }

  onTouchStart(e) {
    this.touchStart = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
    this.data.last = this.data.current;
  }

  onTouchMove(e) {
    e.preventDefault();

    const touchY = e.touches[0].clientY;
    const delta = (this.touchStart.y - touchY) * this.options.touchMultiplier;

    this.data.target = this.data.last + delta;
    this.clampTarget();

    this.startScrolling();
  }

  onTouchEnd() {
    this.touchStart = null;
  }

  clampTarget() {
    this.data.target = Math.max(0, Math.min(this.data.target, this.maxScroll));
  }

  startScrolling() {
    if (!this.isScrolling) {
      this.isScrolling = true;
      this.dom.content.style.willChange = "transform";
    }

    // 스크롤이 멈춘 후 150ms 뒤에 will-change 제거
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      this.stopScrolling();
    }, 150);
  }

  stopScrolling() {
    this.isScrolling = false;
    this.dom.content.style.willChange = "auto";
  }

  run() {
    // Smooth lerp calculation
    this.data.current +=
      (this.data.target - this.data.current) * this.data.ease;
    this.data.rounded = Math.round(this.data.current * 100) / 100;

    const diff = Math.abs(this.data.rounded - this.data.last);

    if (diff > 0.01) {
      const y = -this.data.rounded;
      this.dom.content.style.transform = `translate3d(0,${y}px,0)`;
      this.data.last = this.data.rounded;

      if (typeof ScrollTrigger !== "undefined") {
        ScrollTrigger.update();
      }
    } else if (this.isScrolling) {
      this.data.current = this.data.target;
      this.data.rounded = Math.round(this.data.current);
      const y = -this.data.rounded;
      this.dom.content.style.transform = `translate3d(0,${y}px,0)`;
      this.data.last = this.data.rounded;
    }

    this.requestAnimationFrameId = requestAnimationFrame(this.run);
  }

  scrollTo(target, options = {}) {
    const offset = options.offset || 0;

    if (typeof target === "number") {
      this.data.target = target + offset;
    } else if (typeof target === "string") {
      const el = document.querySelector(target);
      if (el) {
        const rect = el.getBoundingClientRect();
        this.data.target = rect.top + this.data.current + offset;
      }
    }

    this.clampTarget();
  }

  destroy() {
    if (this.requestAnimationFrameId) {
      cancelAnimationFrame(this.requestAnimationFrameId);
    }

    clearTimeout(this.scrollTimeout);

    window.removeEventListener("resize", this.resize);
    window.removeEventListener("wheel", this.onWheel);
    window.removeEventListener("touchstart", this.onTouchStart);
    window.removeEventListener("touchmove", this.onTouchMove);
    window.removeEventListener("touchend", this.onTouchEnd);

    // Reset styles (cssText로 한번에 초기화 - 리플로우 최소화)
    document.body.style.cssText = "";
    this.dom.content.style.cssText = "";
  }
}

// Initialize on page load
let smoothScrollInstance = null;

// Cleanup on page unload
window.addEventListener("beforeunload", () => {
  if (smoothScrollInstance) {
    smoothScrollInstance.destroy();
  }
});
